import { CardMedia, CardMediaProps, Fade, SxProps } from '@mui/material';
import { SchemesListOptionalCallbacks } from '@pages/schemes/components/SchemesList';
import { FC, SyntheticEvent, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import NoDataComponent from '@shared/components/others/NoDataComponent/NoDataComponent';
import {
    SchemePreviewDialogChunk,
    SchemeRequiredFieldsWithOptional,
} from '@pages/schemes/components/SchemePreviewDialogChunk';

interface ImageCheckerProps extends CardMediaProps {
    src: string;
    sx?: SxProps;
    alt?: string;
}

const ImageChecker: FC<ImageCheckerProps> = ({ src, alt, sx, ...props }) => {
    const [isImageLoaded, setIsImageLoaded] = useState<boolean | null>(null);
    const { t } = useTranslation();

    useEffect(() => {
        const img = new Image();
        img.onload = () => setIsImageLoaded(true);
        img.onerror = () => setIsImageLoaded(false);
        img.src = src;
    }, [src]);

    return (
        <>
            {isImageLoaded === false ? (
                <NoDataComponent label={t('preview_loading_error')} {...props} />
            ) : (
                <Fade in={Boolean(isImageLoaded)} timeout={300}>
                    <CardMedia
                        component="img"
                        image={src}
                        alt={alt || 'Изображение'}
                        sx={{
                            height: '100%',
                            objectFit: 'contain',
                            cursor: 'pointer',
                            ...sx,
                        }}
                        {...props}
                    />
                </Fade>
            )}
        </>
    );
};

interface SchemePreviewDialogImageButtonProps {
    scheme: SchemeRequiredFieldsWithOptional;
    sx?: SxProps;
    handleCopyLink?: SchemesListOptionalCallbacks['handleCopyLink'];
    fetchDataOnOpen?: boolean;
}

const SchemePreviewDialog: FC<SchemePreviewDialogImageButtonProps> = ({
    scheme,
    sx,
    handleCopyLink,
    fetchDataOnOpen,
}) => {
    const [open, setOpen] = useState(false);

    const handleOpenDialog = (e: SyntheticEvent) => {
        e.stopPropagation();
        setOpen(true);
    };

    return (
        <>
            <ImageChecker sx={sx} src={scheme.preview || ''} alt="selected_scheme_preview" onClick={handleOpenDialog} />
            {scheme && (
                <SchemePreviewDialogChunk
                    scheme={scheme}
                    open={open}
                    setOpen={setOpen}
                    handleCopyLink={handleCopyLink}
                    fetchDataOnOpen={fetchDataOnOpen}
                />
            )}
        </>
    );
};

export default SchemePreviewDialog;
